import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { 
  Bell, BellOff, Clock, Calendar, CheckCircle, AlertCircle, 
  Settings, Smartphone, Info
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";

const NotificationSettings = () => {
  const { token, user } = useAuth();
  const [permission, setPermission] = useState(() => {
    // Check if Notification API exists before accessing
    if (typeof window !== 'undefined' && 'Notification' in window) {
      return Notification.permission;
    }
    return 'default';
  });
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [registration, setRegistration] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    checkSubscription();
  }, []);

  const checkSubscription = async () => {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      try {
        const reg = await navigator.serviceWorker.ready;
        setRegistration(reg);
        const subscription = await reg.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      } catch (error) {
        console.error('Error checking subscription:', error);
      }
    }
  };

  const requestPermission = async () => {
    // Check if Notification API exists
    if (typeof window === 'undefined' || !('Notification' in window)) {
      setMessage({ type: "error", text: "Notifications not supported on this device" });
      return;
    }
    
    setLoading(true);
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      
      if (result === 'granted') {
        await subscribeUser();
      } else {
        setMessage({ type: "error", text: "Notification permission denied" });
      }
    } catch (error) {
      console.error('Error requesting permission:', error);
      setMessage({ type: "error", text: "Failed to request permission" });
    } finally {
      setLoading(false);
    }
  };

  const subscribeUser = async () => {
    setLoading(true);
    try {
      // Register service worker if not already registered
      let reg = registration;
      if (!reg) {
        reg = await navigator.serviceWorker.register('/sw.js');
        await navigator.serviceWorker.ready;
        setRegistration(reg);
      }

      // Subscribe to push notifications
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(
          // This is a placeholder VAPID key - in production, generate real keys
          'BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZJjSgSnfckjBJuBkr3qBUYIHBQFLXYp5Nksh8U'
        )
      });

      // Send subscription to backend
      await axios.post(`${API}/push-subscription`, {
        subscription: subscription.toJSON(),
        user_id: user.id
      }, { headers });

      setIsSubscribed(true);
      setMessage({ type: "success", text: "Notifications enabled! You'll be reminded every Friday at 8 AM MT." });
    } catch (error) {
      console.error('Error subscribing:', error);
      setMessage({ type: "error", text: "Failed to enable notifications" });
    } finally {
      setLoading(false);
    }
  };

  const unsubscribeUser = async () => {
    setLoading(true);
    try {
      if (registration) {
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
          
          // Remove from backend
          await axios.delete(`${API}/push-subscription/${user.id}`, { headers });
        }
      }
      setIsSubscribed(false);
      setMessage({ type: "success", text: "Notifications disabled" });
    } catch (error) {
      console.error('Error unsubscribing:', error);
      setMessage({ type: "error", text: "Failed to disable notifications" });
    } finally {
      setLoading(false);
    }
  };

  const sendTestNotification = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/push-notification/test`, {
        user_id: user.id
      }, { headers });
      setMessage({ type: "success", text: "Test notification sent!" });
    } catch (error) {
      console.error('Error sending test:', error);
      // Show browser notification directly as fallback
      if (Notification.permission === 'granted') {
        new Notification('Calldrip Scorecards', {
          body: 'Test notification - Time to update your metrics!',
          icon: '/logo192.png'
        });
        setMessage({ type: "success", text: "Test notification shown!" });
      } else {
        setMessage({ type: "error", text: "Failed to send test notification" });
      }
    } finally {
      setLoading(false);
    }
  };

  // Convert VAPID key
  function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  const isPushSupported = typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window;

  return (
    <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
      <div className="flex items-center gap-2 mb-4">
        <Bell className="w-5 h-5 text-amber-400" />
        <h3 className="text-sm font-semibold text-slate-200">Push Notifications</h3>
      </div>

      {!isPushSupported ? (
        <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
          <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-300">
            Push notifications are not supported in this browser. Try Chrome or Edge for the best experience.
          </p>
        </div>
      ) : (
        <>
          {/* Status Card */}
          <div className={`p-3 rounded-lg mb-4 ${
            isSubscribed 
              ? "bg-emerald-500/10 border border-emerald-500/30" 
              : "bg-slate-700/30 border border-slate-600/30"
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {isSubscribed ? (
                  <Bell className="w-4 h-4 text-emerald-400" />
                ) : (
                  <BellOff className="w-4 h-4 text-slate-500" />
                )}
                <span className={`text-sm font-medium ${isSubscribed ? "text-emerald-400" : "text-slate-400"}`}>
                  {isSubscribed ? "Notifications Enabled" : "Notifications Disabled"}
                </span>
              </div>
              <Switch
                checked={isSubscribed}
                onCheckedChange={(checked) => checked ? requestPermission() : unsubscribeUser()}
                disabled={loading}
              />
            </div>
          </div>

          {/* Schedule Info */}
          <div className="flex items-start gap-2 p-3 bg-indigo-500/10 border border-indigo-500/30 rounded-lg mb-4">
            <Clock className="w-4 h-4 text-indigo-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs text-indigo-300 font-medium">Reminder Schedule</p>
              <p className="text-xs text-slate-400 mt-1">
                Every Friday at 8:00 AM Mountain Time
              </p>
            </div>
          </div>

          {/* Test Button */}
          {isSubscribed && (
            <Button
              size="sm"
              variant="outline"
              onClick={sendTestNotification}
              disabled={loading}
              className="w-full text-xs"
            >
              {loading ? "Sending..." : "Send Test Notification"}
            </Button>
          )}

          {/* Permission denied message */}
          {permission === 'denied' && (
            <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg mt-4">
              <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-red-300 font-medium">Permission Blocked</p>
                <p className="text-xs text-slate-400 mt-1">
                  Notifications are blocked. Enable them in your browser settings.
                </p>
              </div>
            </div>
          )}
        </>
      )}

      {/* Status Message */}
      {message.text && (
        <div className={`mt-4 flex items-center gap-2 p-2 rounded-lg text-xs ${
          message.type === "success"
            ? "bg-emerald-500/20 text-emerald-400"
            : "bg-red-500/20 text-red-400"
        }`}>
          {message.type === "success" ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
          <span>{message.text}</span>
        </div>
      )}
    </div>
  );
};

export default NotificationSettings;
